import type { Theme } from '../types/widget';
import { defaultShades, generateShades } from './colorHelpers';
import { config } from './config';
import { STYLE_KEY } from './fontUtils';

const colorKeys = [
  'primary',
  'secondary',
  'positive',
  'negative',
  'fontPrimary',
  'fontSecondary',
  'background',
  'backgroundSoft',
  'accordion',
  'banner',
  'icon',
] as const;
const literalKeys = ['borderRadius', 'shadow'] as const;

const baseVariables = {
  '--color-dark-blue-10': '242 242 245',
  '--color-dark-blue': '0 0 0',
  '--color-dark-grey': '97 95 110',
  '--color-light-grey': '0 79 188',
  '--color-dark': '29 29 29',
  '--color-dark-page': '18 18 18',
  '--font-24': '24px',
  '--font-20': '20px',
  '--font-16': '16px',
  '--color-bg': '#ffffff',
  '--color-bg-soft': '#f3f4f6',
};

function getFontFamilyFromUrl(
  fontUrl: string | undefined,
  defaultFont: 'WidgetPrimaryFont' | 'WidgetSecondaryFont' = 'WidgetPrimaryFont'
): string | null {
  try {
    if (!fontUrl) {
      return defaultFont;
    }
    const url = new URL(fontUrl);
    if (url.hostname === 'fonts.googleapis.com' || url.hostname === 'db.onlinewebfonts.com') {
      const family = url.searchParams.get('family');
      return family ? family.split(':')?.[0] : null;
    }
  } catch (e) {
    // Not a valid URL
  }
  return defaultFont;
}

export function applyTheme(theme: Theme | undefined, element: HTMLElement) {
  if (!element || !theme) {
    return;
  }
  Object.entries(theme).forEach(([key, value]) => {
    if (colorKeys.includes(key as (typeof colorKeys)[number])) {
      const shades = value ? generateShades(value) : defaultShades;
      Object.keys(shades)
        .map(Number)
        .forEach((shade) => {
          element.style.setProperty(`--${key}-color-${shade}`, shades[shade]);
        });
    } else if (literalKeys.includes(key as (typeof literalKeys)[number])) {
      element.style.setProperty(`--${key}`, value);
    }
  });
  Object.entries(baseVariables).forEach(([key, value]) => {
    element.style.setProperty(`${key}`, value);
  });
  const primaryFont = getFontFamilyFromUrl(config.value.theme?.primaryRegularUrl);
  element.style.setProperty(`--widget-font-family`, primaryFont);

  const secondaryFont = getFontFamilyFromUrl(config.value.theme?.secondaryRegularUrl, 'WidgetSecondaryFont');
  element.style.setProperty(`--widget-font-family-secondary`, secondaryFont);
}

export function addGlobalStyles() {
  const theme = config.value.theme;

  if (typeof window === 'undefined' || !theme) return;
  const currentStyle = document.getElementById(STYLE_KEY);
  if (currentStyle) {
    currentStyle.remove();
  }
  const styleElem = document.createElement('style', {
    is: STYLE_KEY,
  });
  styleElem.id = STYLE_KEY;

  styleElem.innerHTML = `
  :root {
     --toastify-color-light: ${theme.background};
     --toastify-color-dark: ${theme.background};
     --toastify-color-info: ${theme.primary};
     --toastify-color-success: ${theme.positive};
     --toastify-color-warning: ${theme.negative};
     --toastify-color-error: ${theme.negative};
     --toastify-color-transparent: rgba(255, 255, 255, 0.7);

     --toastify-icon-color-info: var(--toastify-color-info);
     --toastify-icon-color-success: var(--toastify-color-success);
     --toastify-icon-color-warning: var(--toastify-color-warning);
     --toastify-icon-color-error: var(--toastify-color-error);

     --toastify-toast-width: 320px;
     --toastify-toast-background: ${theme.background};
     --toastify-toast-min-height: 64px;
     --toastify-toast-max-height: 800px;
     --toastify-font-family: sans-serif;
     --toastify-z-index: 9999;

     --toastify-text-color-light: ${theme.fontPrimary};
     --toastify-text-color-dark: ${theme.fontPrimary};

     // Used only for colored theme
     --toastify-text-color-info: #fff;
     --toastify-text-color-success: #fff;
     --toastify-text-color-warning: #fff;
     --toastify-text-color-error: #fff;

     --toastify-spinner-color: ${theme.secondary};
     --toastify-spinner-color-empty-area: #e0e0e0;

     // Used when no type is provided
     // toast("**hello**")
     --toastify-color-progress-light: ${theme.primary};
     // Used when no type is provided
     --toastify-color-progress-dark: ${theme.primary};
     --toastify-color-progress-info: var(--toastify-color-info);
     --toastify-color-progress-success: var(--toastify-color-success);
     --toastify-color-progress-warning: var(--toastify-color-warning);
     --toastify-color-progress-error: var(--toastify-color-error);
  }
  `;
  document.head.append(styleElem);
}
